import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@libs/utils";
import { UserRoleEnum } from "features/users/types";
import { H1, Overlay, PageableList, AccessCheck, ConfirmButton, SubmitButton } from "@components";
import { useNotification } from "@hooks/useNotifications";
import { GridColDef } from "@mui/x-data-grid";
import { TableActionsCell } from "features/statistics/components/TableActionsCell";
import { ReportSummary } from "../types";
import { statisticsService } from "../services/statistics-service";

const columns: GridColDef<ReportSummary>[] = [
  {
    field: "name",
    headerName: "Name",
    flex: 1,
  },
  {
    field: "creationDate",
    headerName: "Date",
    renderCell: ({ row }) => formatDate(row.creationDate),
    flex: 1,
  },
  {
    field: "actions",
    headerName: "Actions",
    renderCell: TableActionsCell,
    align: "center",
    sortable: false,
  },
];

export const StatisticsPage = () => {
  const navigate = useNavigate();
  const { showError } = useNotification();
  const onRowClick = (data: any) => {
    navigate(`/statistics/${data.id}`);
  };

  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const onGenerateReport = async () => {
    try {
      setIsGeneratingReport(true);
      const response = await statisticsService.generateReport();

      navigate(`/statistics/new-report`, { state: { newReport: response } });
    } catch (error) {
      const message = error?.response?.data?.errors?.report?.join(" ");
      showError(message ?? "Failed to generate report.");
      console.error(error);
    }
    setIsGeneratingReport(false);
  };

  return (
    <AccessCheck minAccessLevel={UserRoleEnum.Manager}>
      <Overlay isVisible={isGeneratingReport} />
      <H1 label="Reports List">
        <AccessCheck minAccessLevel={UserRoleEnum.Manager} showAlert={false}>
          <ConfirmButton dialogTitle="" dialogText="Do you want to generate new report?" onConfirm={onGenerateReport}>
            <SubmitButton onClick={() => {}} isProcessing={isGeneratingReport}>
              Generate Report
            </SubmitButton>
          </ConfirmButton>
        </AccessCheck>
      </H1>
      <PageableList
        columns={columns}
        getService={statisticsService.getReports}
        storageId="reportsList"
        disableColumnSorting={false}
        initialRequestParams={{ orderBy: "creationDate", descendingOrder: true }}
        onRowClick={onRowClick}
      />
    </AccessCheck>
  );
};
